import { asc, eq } from "drizzle-orm";
import * as Icons from "@mui/icons-material";
import {
  Button,
  Chip,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";

import { db } from "@/db/db";
import { audioEncoding } from "@/db/schema";
import type { Item } from "@/db/types";
import { useReactiveRelationalQuery } from "@/models/hooks/useReactiveRelationalQuery";

const encodingsQuery = (contentId: string) =>
  db.query.audioEncoding.findMany({
    where: eq(audioEncoding.contentId, contentId),
    orderBy: asc(audioEncoding.language),
  });

export default function AudioEncodingsTable({ item }: { item: Item }) {
  const theme = useTheme();
  const encodings =
    useReactiveRelationalQuery(encodingsQuery, [item.contentId], "AudioEncodingsTable:findManyAudioEncoding") ?? [];

  const exportToCSV = () => {
    if (!encodings?.length) return;

    const header = Object.keys(encodings[0]);
    const rows = encodings.map((obj) =>
      header
        .map((field) => {
          const value = obj[field as keyof typeof obj];
          return typeof value === "string" && value.includes(",") ? `"${value}"` : value;
        })
        .join(","),
    );

    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audio_encodings_${item.contentId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Stack spacing={1}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Language</TableCell>
              <TableCell>Codec</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Voice</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>File</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {encodings.map((row) => (
              <TableRow key={row.id} sx={{ "&:last-child td": { border: 0 } }}>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>{row.language || "original"}</span>
                    {row.translatedFrom && (
                      <Chip
                        label={`from ${row.translatedFrom}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.65rem" }}
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip label={row.codec} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{row.duration ? `${Math.round(Number(row.duration))}s` : "-"}</TableCell>
                <TableCell>{row.generatedVoice || "-"}</TableCell>
                <TableCell>{row.generatedService || "-"}</TableCell>
                <TableCell>{row.transcriptionType || "-"}</TableCell>
                <TableCell>
                  <MuiLink href={row.url} target="_blank" rel="noreferrer">
                    <Icons.PlayCircleOutline fontSize="small" />
                  </MuiLink>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Button
        size="small"
        startIcon={<Icons.Download sx={{ fontSize: 14 }} />}
        onClick={exportToCSV}
        sx={{
          fontSize: "0.75rem",
          textTransform: "none",
          py: 0,
          width: "fit-content",
          alignSelf: "flex-end",
          color: theme.palette.info.main,
          "&:hover": { backgroundColor: `${theme.palette.info.main}20` },
        }}
      >
        Export CSV
      </Button>
    </Stack>
  );
}
